export const API_CONSTANTS = {
  BASE_URL: "https://api.kinzy.club",
} as const;

export const ASSET_URLS = {
  BACK_BUTTON: "https://kinzy.club/games/assets/common/back_button.png",
  MUTE_BUTTON: "https://kinzy.club/games/assets/common/mute_audio_button.png",
  UNMUTE_BUTTON: "https://kinzy.club/games/assets/common/unmute_audio_button.png",
} as const;

export const GAME_SCREENS = {
  START: "start",
  LEVEL_SELECTION: "levelSelection",
  GAMEPLAY: "gameplay",
  LEVEL_COMPLETE: "levelComplete",
  MENU: "menu",
  PLAYING: "playing",
} as const;

export const URL_PARAMS = {
  SET: "set",
  LEVEL: "level",
  QUESTION: "question",
  TOKEN: "token",
} as const;

export const SOUND_TYPES = {
  CLICK: "click",
  SUCCESS: "success",
  ERROR: "error",
  LEVEL_COMPLETE: "levelComplete",
  GAME_START: "gameStart",
} as const;

export const MUSIC_TYPES = {
  EDUCATIONAL: "educational",
  ACTION: "action",
  PUZZLE: "puzzle",
  ADVENTURE: "adventure",
} as const;

export const TTS_CONFIG = {
  AUTO_STOP_TIMEOUT: 30000,
  AUDIO_VOLUME: 0.8,
} as const;

export const LOADING_TIMEOUTS = {
  DEFAULT_TIMEOUT: 10000,
} as const;

export const BUTTON_TEXT = {
  START_GAME: "🎮 Start Game",
  HOW_TO_PLAY: "❓ How to Play",
  NEXT_LEVEL: "Next Level",
  RETRY_LEVEL: "Try Again",
  BACK_TO_MENU: "Back to Menu",
  CONTINUE: "Continue",
  GO_BACK: "Go back",
} as const;

export const UI_TEXT = {
  CHOOSE_LEVEL: "Choose Your Level",
  PROGRESS: "Progress",
  LEVELS: "Levels",
  COMPLETE: "Complete",
  COMPLETED: "Completed",
  UNLOCKED: "Unlocked",
  STARS: "Stars",
  SCORE: "Score",
  LOADING: "Loading...",
} as const;

export const ANIMATIONS = {
  FADE_DURATION: 1000,
  HOVER_SCALE: 1.05,
  ACTIVE_SCALE: 0.95,
  BOUNCE_DELAY: 500,
  HINT_FADE_DURATION: 300,
  BACK_BUTTON_PRESS_DURATION: 150,
} as const;

export const HINT_STYLES = {
  BUTTON_SIZE: "w-14 h-14",
  BUTTON_POSITION: "fixed bottom-6 right-6 z-50",
  BUBBLE_POSITION: "fixed bottom-24 right-6 z-40 max-w-xs",
  BUTTON_GRADIENT: "bg-gradient-to-br from-yellow-400 to-orange-500",
  BUBBLE_GRADIENT: "bg-gradient-to-r from-yellow-50 to-orange-50",
  BUBBLE_BORDER: "border-2 border-yellow-200",
  ARROW_CLASSES: "absolute -bottom-3 right-6 w-6 h-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-r-2 border-b-2 border-yellow-200 transform rotate-45",
  CLOSE_BUTTON: "absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs font-bold hover:bg-red-600 transition-colors duration-200 flex items-center justify-center",
} as const;

export const Z_INDEX = {
  BACK_BUTTON: 50,
  MODAL: 50,
  OVERLAY: 40,
  HINT_BUTTON: 50,
  HINT_BUBBLE: 40,
  DROPDOWN: 30,
  HEADER: 20,
  CONTENT: 10,
} as const;

export const FEEDBACK_MESSAGES = {
  CORRECT: "Correct!",
  NOT_QUITE: "Not quite!",
  CORRECT_ANSWER: "Correct answer:",
  EXPLANATION: "Explanation:",
  CONTINUE: "Continue ➡️",
} as const;

export const SCORE_TEXT = {
  POINTS: "points",
  QUESTION: "Question",
  OF: "of",
} as const;

export const HINT_TEXT = {
  SHOW_HINT: "Show Hint",
  HIDE_HINT: "Hide Hint",
} as const;

export const GAME_STATES = {
  PLAYING: "playing",
  MENU: "menu",
  PAUSED: "paused",
  COMPLETE: "complete",
} as const;

export const AUDIO_DURATIONS = {
  EIGHTH_NOTE: "8n",
  QUARTER_NOTE: "4n",
  HALF_NOTE: "2n",
  THIRTY_SECOND_NOTE: "32n",
} as const;

export const MUSICAL_NOTES = {
  C3: "C3",
  C4: "C4",
  C5: "C5",
  D4: "D4",
  E4: "E4",
  F3: "F3",
  F4: "F4",
  G3: "G3",
  G4: "G4",
  A3: "A3",
  A4: "A4",
  B4: "B4",
} as const;

export const EXAMPLE_GAME_PROGRESS = {
  unlockedLevel: 5,
  completedLevels: [1, 2, 3, 4],
  levelScores: {
    1: 100,
    2: 95,
    3: 88,
    4: 92,
  },
  levelStars: {
    1: 3,
    2: 3,
    3: 2,
    4: 3,
  },
  currentSet: 1,
} as const;

export const STORAGE_ERROR_HANDLING_FEATURES = [
  "Automatic fallback to default progress on corruption",
  "Data validation and type checking",
  "Cleanup of corrupted localStorage entries",
  "UUID fallback for storage keys",
  "Comprehensive error logging",
  "Safe operations that never throw exceptions",
  "Context error boundaries for hook usage",
] as const;

export const STORAGE_INTEGRATION_INFO = {
  APP_LEVEL: "Wrap your game app with StorageProvider",
  COMPONENT_LEVEL: "Use useStorage() hook in any child component",
  STORAGE_KEY: "Automatically generated from URL game ID",
  DATA_STRUCTURE: "Consistent GameProgress interface",
  PERFORMANCE: "Context-based, minimal re-renders",
} as const;

export const AUDIO_DEBUG = {
  LOG_PREFIX: "[TTS]",
  CONTENT_PREVIEW_LENGTH: 50,
  DUPLICATE_PREVENTION_DELAY: 1000,
  FEEDBACK_SPEECH_DELAY: 500,
  ERROR_PREFIX: "[TTS-ERROR]",
  CACHE_PREFIX: "[TTS-CACHE]",
  NETWORK_PREFIX: "[TTS-NETWORK]",
} as const;

export const ERROR_HANDLING = {
  TTS_SILENT_FAIL: true,
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 2000,
  GRACEFUL_DEGRADATION: true,
  LOG_ERRORS: true,
  SHOW_ERROR_TOAST: false,
} as const;

export const AUDIO_MUTE = {
  STORAGE_PREFIX: "kinzy_audio_muted_",
  DEFAULT_MUTED: false,
  BUTTON_SIZE: "w-12 h-12",
  BUTTON_POSITION: "fixed top-4 right-4 z-40",
  BUTTON_GRADIENT: "bg-gradient-to-br from-blue-500 to-purple-600",
  BUTTON_HOVER: "hover:from-blue-600 hover:to-purple-700",
  ICON_SIZE: "w-5 h-5",
  LOG_PREFIX: "[AUDIO_MUTE]",
} as const;

export const SCORING = {
  POINTS_PER_QUESTION: 10,
  STAR_THRESHOLDS: {
    THREE_STARS: 0.9,
    TWO_STARS: 0.7,
    ONE_STAR: 0.5,
    ZERO_STARS: 0,
  },
  LEVEL_UNLOCK_THRESHOLD: 0.7,
} as const;

export const LEVEL_PROGRESSION = {
  AUTO_SAVE_ON_COMPLETE: true,
  SAVE_ON_CLOSE: true,
  SAVE_ON_RETRY: true,
  UPDATE_UI_IMMEDIATELY: true,
} as const;

export const BACKGROUND_IMAGE_URL = "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1920&q=80" as const;

export const DATA_INTEGRATION = {
  DEFAULT_TIMEOUT: 10000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 2000,
  CACHE_DURATION: 300000,
  ERROR_MESSAGES: {
    FETCH_FAILED: "Failed to load game data",
    NETWORK_ERROR: "Network error occurred",
    PARSE_ERROR: "Failed to parse game data",
    TIMEOUT_ERROR: "Request timed out",
    INVALID_DATA: "Invalid game data format",
  },
  LOADING_STATES: {
    INITIAL: "Loading game data...",
    RETRYING: "Retrying...",
    PARSING: "Processing data...",
  },
} as const;

export type GameScreen = (typeof GAME_SCREENS)[keyof typeof GAME_SCREENS];
export type URLParam = (typeof URL_PARAMS)[keyof typeof URL_PARAMS];
export type SoundType = (typeof SOUND_TYPES)[keyof typeof SOUND_TYPES];
export type MusicType = (typeof MUSIC_TYPES)[keyof typeof MUSIC_TYPES];
export type GameState = (typeof GAME_STATES)[keyof typeof GAME_STATES];
export type AudioDuration = (typeof AUDIO_DURATIONS)[keyof typeof AUDIO_DURATIONS];
export type MusicalNote = (typeof MUSICAL_NOTES)[keyof typeof MUSICAL_NOTES];
export type TTSConfig = (typeof TTS_CONFIG)[keyof typeof TTS_CONFIG];
export type LoadingTimeout = (typeof LOADING_TIMEOUTS)[keyof typeof LOADING_TIMEOUTS];
export type AudioDebug = (typeof AUDIO_DEBUG)[keyof typeof AUDIO_DEBUG];
export type ErrorHandling = (typeof ERROR_HANDLING)[keyof typeof ERROR_HANDLING];
export type AudioMute = (typeof AUDIO_MUTE)[keyof typeof AUDIO_MUTE];
export type Scoring = (typeof SCORING)[keyof typeof SCORING];
export type DataIntegration = (typeof DATA_INTEGRATION)[keyof typeof DATA_INTEGRATION];
export type LevelProgression = (typeof LEVEL_PROGRESSION)[keyof typeof LEVEL_PROGRESSION];